import { t, catName } from "../i18n";

export default function SceneCard({ scene, onClick, lang = 'zh', trackCount }) {
  const formatDuration = (seconds) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, "0")}`;
  };

  const categoryColors = {
    relaxing: "bg-emerald-100 text-emerald-700",
    tense: "bg-amber-100 text-amber-700",
    horror: "bg-purple-100 text-purple-700",
    poetry: "bg-pink-100 text-pink-700",
    "female-lavish": "bg-cyan-100 text-cyan-700",
    "male-deep": "bg-lime-100 text-lime-700",
  };

  return (
    <div
      onClick={onClick}
      className="group cursor-pointer bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
    >
      {/* Image */}
      <div className="relative h-44 overflow-hidden">
        <img
          src={scene.image}
          alt={lang === "en" ? scene.nameEn || scene.name : scene.name}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />

        {/* Duration badge */}
        <div className="absolute top-3 right-3 px-2 py-1 bg-black/50 backdrop-blur text-white text-xs rounded-lg font-mono">
          {formatDuration(scene.duration)}
        </div>

        {/* Play button overlay */}
        <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
          <div className="w-14 h-14 rounded-full bg-white/90 backdrop-blur flex items-center justify-center shadow-lg">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor" className="text-indigo-600 ml-1">
              <path d="M8 5v14l11-7z" />
            </svg>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-4">
        <div className="flex items-start justify-between gap-2 mb-2">
          <h3 className="font-bold text-gray-900 group-hover:text-indigo-600 transition-colors">
            {lang === "en" ? scene.nameEn || scene.name : scene.name}
          </h3>
          <span className={`text-xs px-2 py-0.5 rounded-full font-medium shrink-0 ${categoryColors[scene.category] || "bg-gray-100 text-gray-600"}`}>
            {catName(lang, scene.category, scene.category)}
          </span>
        </div>

        <p className="text-sm text-gray-500 mb-3 line-clamp-2">
          {lang === "en" ? scene.descriptionEn || scene.description : scene.description}
        </p>

        {/* Tags */}
        <div className="flex flex-wrap gap-1.5">
          {((lang === "en" && scene.tagsEn) ? scene.tagsEn : scene.tags).slice(0, 3).map((tag) => (
            <span
              key={tag}
              className="text-xs px-2 py-0.5 bg-gray-100 text-gray-500 rounded-md"
            >
              {tag}
            </span>
          ))}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-100">
          <div className="text-xs text-gray-400">
            ▶ {scene.plays?.toLocaleString()}
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-400 flex items-center gap-1">
              🎵 {trackCount ?? '...'}
            </span>
            <a
              href={`/scenes/${scene.atmo}/`}
              className="text-xs px-2.5 py-1 bg-indigo-50 text-indigo-600 rounded-lg hover:bg-indigo-100 transition font-medium"
              onClick={(e) => e.stopPropagation()}
            >
              {t(lang, 'sceneBtn')}
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
